
import { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { setAppLanguage } from "../../redux/action/lang.action";
import { login } from "../../redux/action/login.action";
import { toast } from "../../components/AppToast";
import i18n from "../../Localization/i18n";
import { saveBrandPreferences } from "../../services/brand/brand";
import {
  BRANDING_SOURCE,
  DEFAULT_PREFERENCES,
  applyThemePreferences,
  buildSidebarGradient,
  pickPreferences,
  resolveActiveBranding,
} from "../../utils/branding";

const languages = [
  { value: "en", label: "English" },
  { value: "es", label: "Spanish" },
  { value: "fr", label: "French" },
  { value: "de", label: "German" },
  { value: "hi", label: "Hindi" },
  { value: "ar", label: "Arabic" },
];

const colorControls = [
  { key: "importBtnColor", label: "Import" },
  { key: "yesBtnColor", label: "Confirm" },
  { key: "noBtnColor", label: "Decline" },
];

const BRANDING_SOURCE_LABEL = {
  [BRANDING_SOURCE.PARTNER]: "Partner branding",
  [BRANDING_SOURCE.TENANT]: "Tenant branding",
  [BRANDING_SOURCE.DEFAULT]: "Default branding",
};

const PreferencesCard = ({
  onSaved,
  scopeType = "TENANT",
  scopeId = "",
  initialPreferences = null,
}) => {
  const dispatch = useDispatch();
  const { userDetail } = useSelector((state) => state.login);

  // Branding that applies to the logged-in user:
  // partnerBranding -> tenantBranding -> defaults.
  const activeBranding = useMemo(
    () => resolveActiveBranding(userDetail),
    [userDetail]
  );

  const [preferences, setPreferences] = useState(() => ({
    ...activeBranding.preferences,
  }));

  const [saving, setSaving] = useState(false);

  // Populate the form. Priority (lowest -> highest):
  //   defaults -> branding from login response -> document from GET /brand.
  useEffect(() => {
    const nextPreferences = {
      ...DEFAULT_PREFERENCES,
      ...activeBranding.preferences,
      ...pickPreferences(initialPreferences),
    };

    setPreferences(nextPreferences);
    applyAllPreferences(nextPreferences);
  }, [initialPreferences, scopeType, scopeId, activeBranding]);

  const applyAllPreferences = (values) => {
    applyThemePreferences(values);
  };

  // Write saved preferences back into userDetail.branding for the
  // current scope (PARTNER -> partnerBranding, TENANT -> tenantBranding).
  const syncBrandingToStore = (savedPreferences) => {
    if (!userDetail) return;

    const brandingKey =
      scopeType === "PARTNER" ? "partnerBranding" : "tenantBranding";

    const isWrapped = Boolean(userDetail?.data);
    const user = userDetail?.data || userDetail;
    const currentBranding = user?.branding || {};
    const currentScope = currentBranding[brandingKey] || {};

    const nextUser = {
      ...user,
      branding: {
        ...currentBranding,
        [brandingKey]: {
          ...currentScope,
          preferences: {
            ...(currentScope.preferences || {}),
            ...savedPreferences,
          },
        },
      },
    };

    dispatch(login(isWrapped ? { ...userDetail, data: nextUser } : nextUser));
  };

  const applyPreview = (name, value, currentPreferences) => {
    const next = {
      ...currentPreferences,
      [name]: value,
    };

    if (name === "importBtnColor") {
      document.documentElement.style.setProperty(
        "--import-gradient",
        value
      );
    }

    if (name === "yesBtnColor") {
      document.documentElement.style.setProperty(
        "--yes-gradient",
        value
      );
    }

    if (name === "noBtnColor") {
      document.documentElement.style.setProperty(
        "--no-gradient",
        value
      );
    }

    if (
      name === "themeGradientStart" ||
      name === "themeGradientEnd"
    ) {
      applyThemePreferences(next);
    }
  };

  const handlePreferencesChange = (event) => {
    const { name, type, checked, value } = event.target;

    const nextValue = type === "checkbox" ? checked : value;

    setPreferences((previous) => {
      const next = {
        ...previous,
        [name]: nextValue,
      };

      applyPreview(name, nextValue, previous);

      return next;
    });
  };

  const handleSavePreferences = async () => {
    if (!scopeId || !scopeType) {
      toast.error("Unable to identify the tenant or partner.");
      return;
    }

    // Send only fields supported by the preferences API.
    // Logo and ownership fields are never included.
    const payload = {
      language: preferences.language,
      notifications: preferences.notifications,
      notificationPreference: preferences.notificationPreference,
      themeGradientStart: preferences.themeGradientStart,
      themeGradientEnd: preferences.themeGradientEnd,
      importBtnColor: preferences.importBtnColor,
      yesBtnColor: preferences.yesBtnColor,
      noBtnColor: preferences.noBtnColor,
    };

    try {
      setSaving(true);

      const response = await saveBrandPreferences(payload);

      const savedPreferences = response?.data;

      if (!response?.success) {
        throw new Error(
          response?.message || "Failed to save preferences."
        );
      }

      // Use the values returned by the server.
      const nextPreferences = {
        ...preferences,
        ...(savedPreferences || {}),
      };

      setPreferences((previous) => ({
        ...previous,
        ...nextPreferences,
      }));

      applyAllPreferences(nextPreferences);

      const language = nextPreferences.language || "en";

      await i18n.changeLanguage(language);
      dispatch(setAppLanguage(language));

      // Keep compatibility with existing app language handling.
      localStorage.setItem("appLanguage", language);

      document.documentElement.dir =
        language === "ar" ? "rtl" : "ltr";

      document.documentElement.lang = language;

      // Keep the logged-in branding in sync so the global theme
      // (applied from redux) reflects the saved preferences.
      syncBrandingToStore(pickPreferences(nextPreferences));

      onSaved?.(savedPreferences || nextPreferences);
    } catch (error) {
      console.error("Failed to save preferences:", error);
      toast.error(error.message || "Failed to save preferences.");
    } finally {
      setSaving(false);
    }
  };

  const gradient = buildSidebarGradient(
    preferences.themeGradientStart,
    preferences.themeGradientEnd
  );

  return (
    <div className="settings-form">
      <div className="settings-theme">
        <div className="settings-branding-source">
          <div className="settings-branding-source__logo">
            {activeBranding.brandImage ? (
              <img
                key={activeBranding.brandImage}
                src={activeBranding.brandImage}
                alt={`${BRANDING_SOURCE_LABEL[activeBranding.source]} logo`}
                onError={(e) => {
                  e.currentTarget.onerror = null;
                  e.currentTarget.src = "/logo.png";
                }}
              />
            ) : (
              <img src="/logo.png" alt="Default logo" />
            )}
          </div>

          <div className="settings-branding-source__copy">
            <strong>{BRANDING_SOURCE_LABEL[activeBranding.source]}</strong>
            <span>
              {activeBranding.source === BRANDING_SOURCE.DEFAULT
                ? "No partner or tenant branding configured. Default preferences are applied."
                : `Preferences below are loaded from the ${
                    activeBranding.source === BRANDING_SOURCE.PARTNER
                      ? "partner"
                      : "tenant"
                  } branding of the logged-in user.`}
            </span>
          </div>
        </div>

        <div className="settings-theme__copy">
          <h3>General preferences</h3>
          <p>
            Set the language and notification behavior for your workspace.
          </p>
        </div>

        <div className="mb-3">
          <label className="form-label" htmlFor="brand-language">
            Language
          </label>

          <select
            id="brand-language"
            className="form-select"
            name="language"
            value={preferences.language}
            onChange={handlePreferencesChange}
          >
            {languages.map((language) => (
              <option key={language.value} value={language.value}>
                {language.label}
              </option>
            ))}
          </select>
        </div>

        <div className="mb-3">
          <label className="form-label" htmlFor="brand-notification-mode">
            Notification preference
          </label>

          <select
            id="brand-notification-mode"
            className="form-select"
            name="notificationPreference"
            value={preferences.notificationPreference}
            onChange={handlePreferencesChange}
          >
            <option value="email">Email</option>
            <option value="in_app">In-app</option>
            <option value="both">Email and in-app</option>
            <option value="none">None</option>
          </select>
        </div>

        <div className="form-check form-switch mb-4">
          <input
            id="brand-notifications"
            className="form-check-input"
            type="checkbox"
            name="notifications"
            checked={preferences.notifications}
            onChange={handlePreferencesChange}
          />

          <label
            className="form-check-label"
            htmlFor="brand-notifications"
          >
            Enable notifications
          </label>
        </div>

        <div className="settings-theme__copy">
          <h3>Portal theme</h3>
          <p>
            Customize the sidebar and primary action colors.
            Changes preview immediately and are saved to your workspace.
          </p>
        </div>

        <div
          className="settings-theme__preview"
          style={{ background: gradient }}
        >
          <span>Preview</span>
        </div>

        <div className="settings-swatches">
          <label className="settings-swatch">
            <span>Start</span>
            <input
              type="color"
              name="themeGradientStart"
              value={preferences.themeGradientStart}
              onChange={handlePreferencesChange}
            />
            <em>{preferences.themeGradientStart}</em>
          </label>

          <label className="settings-swatch">
            <span>End</span>
            <input
              type="color"
              name="themeGradientEnd"
              value={preferences.themeGradientEnd}
              onChange={handlePreferencesChange}
            />
            <em>{preferences.themeGradientEnd}</em>
          </label>

          {colorControls.map((control) => (
            <label className="settings-swatch" key={control.key}>
              <span>{control.label}</span>
              <input
                type="color"
                name={control.key}
                value={preferences[control.key]}
                onChange={handlePreferencesChange}
              />
              <em>{preferences[control.key]}</em>
            </label>
          ))}
        </div>

        <div className="settings-preview-row">
          <span
            className="settings-chip"
            style={{ background: preferences.importBtnColor }}
          >
            Import
          </span>

          <span
            className="settings-chip"
            style={{ background: preferences.yesBtnColor }}
          >
            Yes
          </span>

          <span
            className="settings-chip"
            style={{ background: preferences.noBtnColor }}
          >
            No
          </span>
        </div>
      </div>

      <div className="settings-actions">
        <button
          type="button"
          className="settings-btn settings-btn--primary"
          onClick={handleSavePreferences}
          disabled={saving}
        >
          {saving ? "Saving..." : "Save preferences"}
        </button>
      </div>
    </div>
  );
};

export default PreferencesCard;
